import BoltIcon from "@mui/icons-material/Bolt";
import EditNoteIcon from "@mui/icons-material/EditNote";
import EventIcon from "@mui/icons-material/Event";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import { Box, Card, Stack, Typography, alpha } from "@mui/material";
import { describeNext, folderText, updatedText } from "../program-landing";
import {
  AccessPill,
  FavouriteButton,
  MatchBadge,
  ProgramBadge,
  ProgramTitleLink,
} from "./program-parts";

import type { AccessData } from "../../program-access/hooks/use-program-access";
import type { ProgramTree } from "../../program-tree/program-tree";
import type { ProgramSummary } from "../program-landing";
import type { FC } from "react";

const nextIcons = {
  send: EventIcon,
  always: BoltIcon,
  nothing: EventBusyIcon,
  empty: EditNoteIcon,
} as const;

const Stat: FC<{ label: string; value: number; color?: string }> = ({ label, value, color }) => (
  <Stack spacing={0.25} sx={{ minWidth: 0 }}>
    <Typography variant="h6" component="span" sx={{ color: value && color ? color : undefined }}>
      {value}
    </Typography>
    <Typography variant="caption" noWrap sx={{ color: "text.secondary" }}>
      {label}
    </Typography>
  </Stack>
);

/**
 * A program on the landing page. Its title opens the program, and covers the card. The access shows
 * only with the access data, which only admins can read.
 */
export const ProgramCard: FC<{
  summary: ProgramSummary;
  tree: ProgramTree;
  access?: AccessData;
  /** The titles of the folders that match the search. */
  matched: string[];
  today: Date;
  onToggleFavourite: () => void;
}> = ({ summary, tree, access, matched, today, onToggleFavourite }) => {
  const { node } = summary;
  const updated = updatedText(summary, today);
  const next = describeNext(summary);
  const NextIcon = nextIcons[next.kind];

  return (
    <Card
      variant="outlined"
      sx={{
        position: "relative",
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        transition: "box-shadow .15s ease-out, border-color .15s ease-out",
        "&:hover": {
          boxShadow: 3,
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.3),
        },
        "&:hover .program-title": { color: "primary.dark" },
      }}
    >
      <Box aria-hidden sx={{ height: 4, bgcolor: node.theme_colour }} />
      <Stack spacing={1.75} useFlexGap sx={{ flex: 1, px: 2.5, pt: 2.5, pb: 2 }}>
        <Stack direction="row" spacing={1.75} sx={{ alignItems: "flex-start" }}>
          <ProgramBadge node={node} size={48} />
          <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              component="h2"
              sx={{
                fontWeight: 700,
                overflowWrap: "anywhere",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              <ProgramTitleLink node={node} />
            </Typography>
            {updated ? (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {updated}
              </Typography>
            ) : null}
          </Stack>
          <Box sx={{ mt: -0.75, mr: -1 }}>
            <FavouriteButton
              node={node}
              favourite={summary.favourite}
              onToggle={onToggleFavourite}
            />
          </Box>
        </Stack>

        {matched.length ? <MatchBadge folders={matched} /> : null}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: 1,
            py: 1.5,
            borderTop: 1,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Stat label="Communications" value={summary.communications} />
          <Stat label="Active" value={summary.active} color="success.dark" />
          <Stat label="Drafts" value={summary.drafts} color="warning.dark" />
          <Stat label="Folders" value={summary.folders.length} />
        </Box>

        <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
          <NextIcon
            aria-hidden
            sx={{
              fontSize: 18,
              mt: 0.125,
              color: next.kind === "send" ? node.theme_colour : "text.secondary",
            }}
          />
          <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
            <Box component="span" sx={{ fontWeight: 600 }}>
              {next.head}
            </Box>{" "}
            {next.body}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          useFlexGap
          sx={{ mt: "auto", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}
        >
          <Stack direction="row" spacing={0.75} sx={{ flex: 1, minWidth: 0, alignItems: "center" }}>
            <FolderOutlinedIcon aria-hidden sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography variant="caption" noWrap sx={{ color: "text.secondary" }}>
              {folderText(node)}
            </Typography>
          </Stack>
          {access ? <AccessPill data={access} tree={tree} node={node} /> : null}
        </Stack>
      </Stack>
    </Card>
  );
};
